import * as React from 'react';
import { useDispatch } from 'react-redux';

import { alertError, alertSuccess } from '../../../../store/reducers/Alert';
import { CommentButton } from '../../../../components/buttons/CommentButton';
import { ModelAction } from '../../../../types/responses/ModelAction';
import { ContractorProjectDocFolderActions } from '../../../../helpers/constants/modelActions/ContractorProjectDocFolderActions';
import { services } from '../../../../services';
import { useBackgroundPromiseWrapper } from '../../../../helpers/hooks/useBackgroundPromiseWrapper';
import { ValidationResult } from '../../../../types/models/error/ValidationResult';
import { WorkflowInstance } from '../../../workflows/models/WorkflowInstance';
import { ConfirmButton } from './helpers/ConfirmButton';

interface FolderFilesActionsProps {
    actions: ModelAction[];
    projectDocFolderId: number;
    filesCount: number;
    onCurrentFolderStatusUpdated: () => void;
    onDeleted?: () => void;
}

export const FolderFilesActions: React.FC<FolderFilesActionsProps> = ({
    actions,
    projectDocFolderId,
    filesCount,
    onCurrentFolderStatusUpdated,
    onDeleted
}) => {
    const dispatch = useDispatch();
    const [promiseWrapper, isLoading] = useBackgroundPromiseWrapper();

    const sendToApprove = React.useCallback(
        (workflowId: number, comment: string) => {
            promiseWrapper(
                () => services.workflowInstanceService.create(workflowId, projectDocFolderId, comment),
                (res: WorkflowInstance) => {
                    dispatch(alertSuccess({ detail: `Отправлено на согласование` }));
                    if (res.Id && res.Id > 0) onCurrentFolderStatusUpdated();
                    window.location.reload();
                }
            );
            // eslint-disable-next-line
        },
        [projectDocFolderId, onCurrentFolderStatusUpdated]
    );

    const getSendToApprove = React.useCallback(
        (workflowId: number) => {
            return (comment: string) => sendToApprove(workflowId, comment);
        },
        [sendToApprove]
    );

    const cancel = React.useCallback(
        (workflowId: number, comment: string) => {
            promiseWrapper(
                () => services.workflowInstanceService.cancel(workflowId, projectDocFolderId, comment),
                (res: ValidationResult) => {
                    if (res?.Errors?.length > 0)
                        dispatch(alertError({ detail: `Ошибки: ${res.Errors.map((e) => e.Message).join('; ')}` }));
                    else {
                        dispatch(alertSuccess({ detail: `Отозвано с согласования` }));
                        onCurrentFolderStatusUpdated();
                        window.location.reload();
                    }
                }
            );
            // eslint-disable-next-line
        },
        [projectDocFolderId, onCurrentFolderStatusUpdated]
    );

    const getCancel = React.useCallback(
        (workflowId: number) => {
            return (comment: string) => cancel(workflowId, comment);
        },
        [cancel]
    );

    return (
        <div className="pd-file-actions">
            {actions.filter(
                (action) =>
                    action.Id === ContractorProjectDocFolderActions.SendForApproval ||
                    action.Id === ContractorProjectDocFolderActions.CancelApproval
            ).length > 0 &&
                actions.map((action) => {
                    switch (action.Id) {
                        case ContractorProjectDocFolderActions.SendForApproval:
                            return (
                                <CommentButton
                                    label="Отправить на согласование"
                                    className="pd-file-action-btn pd-file-action-primary"
                                    icon="fa-solid fa-paper-plane"
                                    key={action.Id}
                                    commentIsRequired={false}
                                    disabled={isLoading || filesCount === 0}
                                    action={getSendToApprove(action.ObjectId)}
                                />
                            );

                        case ContractorProjectDocFolderActions.CancelApproval:
                            return (
                                <CommentButton
                                    label="Отозвать с согласования"
                                    className="pd-file-action-btn pd-file-action-danger"
                                    icon="fa-solid fa-rotate-left"
                                    key={action.Id}
                                    commentIsRequired={false}
                                    disabled={isLoading}
                                    action={getCancel(action.ObjectId)}
                                />
                            );

                        default:
                            return <React.Fragment key={action.Id}></React.Fragment>;
                    }
                })}
            {actions.some((a) => a.Id === ContractorProjectDocFolderActions.DeleteDocument) && (
                <ConfirmButton
                    icon="fa-solid fa-trash"
                    label="Удалить документ"
                    header="Удаление документа"
                    message="Вы уверены, что хотите удалить документ?"
                    acceptLabel="Удалить"
                    acceptIcon="fa-solid fa-trash"
                    disabled={isLoading}
                    action={() => {
                        promiseWrapper(
                            () => services.projectDocFolderService.delete([projectDocFolderId]),
                            () => {
                                dispatch(alertSuccess());
                                onDeleted?.();
                            }
                        );
                    }}
                />
            )}
        </div>
    );
};
